import { html, LitElement } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import { Q1MdTextareaStyles } from './q1-md-textarea-styles.js';

import { FormInputMixin } from '../../modules/form-input-mixin.js';

import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '../../components/q1-markdown/q1-markdown.js';

export class Q1MdTextarea extends FormInputMixin(LitElement) {
  static styles = [
    Q1MdTextareaStyles,
  ];

  static properties = {
    ...super.properties, // label, name, value, disabled, required, valid, helpText, _internals

    maxlength: { type: Number, reflect: true },
    rows: { type: Number, reflect: true },
    resize: { type: String, reflect: true }, // none, vertical, auto

    input: { type: Object },
  };

  constructor() {
    super();

    this.maxlength = '';
    this.rows = 4;
    this.resize = 'vertical';
    
    this.input = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.updateFormValue();
  }

  firstUpdated() {
    this.updateComplete.then(() => {
      this.input = this.renderRoot.getElementById('input');
  
      this.updateValidity(); // can't run until this.input exists
    });
  }

  /*=== STANDARD FORM IMPLEMENTATION ===*/

  formResetCallback() {
    this.input.value = '';
    
    this.handleInput();
    this.handleChange();
  }

  // FIXME >> Should be moved partially to the mixin somehow. Wait for multiple examples.
  updateValidity() {
    this._internals.setValidity(this.input.validity, this.input.validationMessage, this.input);
  }

  /*=== HANDLERS ===*/
  
  handleInput() {
    this.value = this.input.value;
    this.valid = this.input.checkValidity();
    this.updateFormValue();
    this.updateValidity();

    super.handleInput(); // dispatches the event
  }

  handleTabShow(event) {
    event.stopPropagation();
  }  

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <label id="input-label" for="input">${this.label}</label>
      <sl-tab-group @sl-tab-show=${this.handleTabShow}>
        <sl-tab slot="nav" panel="edit" active>Edit</sl-tab>
        <sl-tab slot="nav" panel="preview">Preview</sl-tab>

        <sl-tab-panel name="edit">
          <sl-textarea
            id="input"
            name="input"
            value=${this.value}
            @sl-change=${this.handleChange}
            @sl-input=${this.handleInput}
            maxlength=${this.maxlength}
            rows=${this.rows}
            resize=${this.resize}
            ?disabled=${this.disabled}
            ?required=${this.required}
          >
            <span slot="help-text">
              ${this.helpText ? this.helpText : html`
                You can use
                <a
                  href="https://www.markdownguide.org/basic-syntax/"
                  target="_blank"
                >markdown syntax</a> 
                to add headers, lists, and other formatting.
              `}
            </span>
          </sl-textarea>
        </sl-tab-panel>

        <sl-tab-panel name="preview">
          <q1-markdown
            .content=${this.value}
            style=${styleMap({ '--rows': this.rows })}
          ></q1-markdown>
        </sl-tab-panel>
      </sl-tab-group>
    `;
  }
}

customElements.define('q1-md-textarea', Q1MdTextarea);
